import { Session, User } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../integrations/supabase/client';
import { logRocketService } from '../lib/logrocket';

// Define the context types
interface AuthContextType {
  user: User | null;
  session: Session | null;
  tenantId: string | null;
  role: string | null;
  isLoading: boolean;
  isCheckingTenantStatus: boolean;
  isSigningIn: boolean;
  isSystemAdmin: boolean;
  isTenantAdmin: boolean;
  isTenantInterviewer: boolean;
  requiresPasswordReset: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: (options?: { skipNavigation?: boolean }) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUserData: (userId?: string) => Promise<void>;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create a provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isCheckingTenantStatus, setIsCheckingTenantStatus] = useState(false);
  const isSigningInRef = React.useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize auth state - rely solely on onAuthStateChange for all auth state updates
    // This prevents race conditions between manual session fetching and auth event handling

    // Periodic check for tenant status (every minute)
    const checkTenantStatus = async () => {
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();

      if (currentSession?.user) {
        const { data: userData } = await supabase
          .from('users')
          .select('role, tenant:tenants!users_tenant_id_fkey(status, deactivation_reason)')
          .eq('id', currentSession.user.id)
          .maybeSingle();

        if (
          userData?.tenant &&
          userData.tenant.status === 'inactive' &&
          userData.role !== 'system_admin'
        ) {
          const reason = userData.tenant.deactivation_reason || 'Account deactivated';
          await supabase.auth.signOut();
          setUser(null);
          setSession(null);
          setTenantId(null);
          setRole(null);
          toast.error(`Your organization has been deactivated: ${reason}. Please contact support.`);
          navigate('/login');
        }
      }
    };

    // Check tenant status every minute
    const intervalId = setInterval(checkTenantStatus, 60000);

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      // If we're in the middle of signing in or checking tenant status, don't set session state yet
      // The signIn function will handle setting the state after tenant status check
      if (
        (isSigningInRef.current || isSigningIn || isCheckingTenantStatus) &&
        event === 'SIGNED_IN'
      ) {
        return;
      }

      // For SIGNED_OUT events, always clear state regardless of flags
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setTenantId(null);
        setRole(null);
        setIsLoading(false);
        setIsSigningIn(false);
        setIsCheckingTenantStatus(false);
        isSigningInRef.current = false;
        return;
      }

      // Skip if we already have the exact same session to avoid duplicate processing
      // Only skip if we actually have a session (not undefined/null on both sides)
      if (
        newSession?.user?.id && // Only skip if new session has a user
        session?.user?.id === newSession?.user?.id &&
        session?.access_token === newSession?.access_token &&
        session?.refresh_token === newSession?.refresh_token
      ) {
        return;
      }

      setSession(newSession);
      setUser(newSession?.user || null);

      // Get metadata values
      const userTenantId = newSession?.user?.app_metadata?.tenant_id;
      const userRole = newSession?.user?.app_metadata?.role;

      if (newSession?.user) {
        // If role is not in app_metadata, query the database
        // This handles: new signups after onboarding, users whose metadata hasn't synced yet
        if (!userRole) {
          try {
            const { data, error } = await supabase
              .from('users')
              .select('tenant_id, role')
              .eq('id', newSession.user.id)
              .maybeSingle();

            if (error) {
              console.error('Error fetching user data:', error);
              // If query fails, user might not have a record yet (brand new signup without onboarding)
              setTenantId(userTenantId || null);
              setRole(userRole || null);
              setIsLoading(false);
            } else if (data) {
              setTenantId(data.tenant_id || userTenantId || null);
              setRole(data.role || userRole || null);

              // Identify user in LogRocket with additional context
              if (newSession.user.email) {
                logRocketService.identify(newSession.user.email, {
                  userId: newSession.user.id,
                  role: data.role || userRole,
                  tenantId: data.tenant_id || userTenantId,
                  signInMethod: 'database_lookup',
                });
              }
            } else {
              // No database record found - brand new signup without onboarding
              setTenantId(null);
              setRole(null);
            }
            setIsLoading(false);
          } catch (error) {
            console.error('Database query failed:', error);
            // Fallback to metadata
            setTenantId(userTenantId || null);
            setRole(userRole || null);
            setIsLoading(false);
          }
        } else {
          setTenantId(userTenantId);
          setRole(userRole);
          setIsLoading(false);

          // Identify user in LogRocket with metadata
          if (newSession.user.email) {
            logRocketService.identify(newSession.user.email, {
              userId: newSession.user.id,
              role: userRole,
              tenantId: userTenantId,
              signInMethod: 'metadata',
            });
          }
        }
      } else {
        // No user, clear everything
        setTenantId(null);
        setRole(null);
        setIsLoading(false);
      }
    });

    const AUTH_INIT_TIMEOUT_MS = 10_000;
    const safetyTimeout = setTimeout(async () => {
      setIsLoading(current => {
        if (!current) return current;
        console.warn(
          '[Auth] Safety timeout: auth initialization did not complete within 10s, clearing stale session'
        );
        supabase.auth.signOut().catch(() => {});
        setSession(null);
        setUser(null);
        setTenantId(null);
        setRole(null);
        return false;
      });
    }, AUTH_INIT_TIMEOUT_MS);

    // Cleanup subscription
    return () => {
      authListener.subscription.unsubscribe();
      clearInterval(intervalId);
      clearTimeout(safetyTimeout);
    };
  }, [navigate]);

  // Function to refresh user data from database
  const refreshUserData = async (userId?: string) => {
    const targetUserId = userId || user?.id || session?.user?.id;
    if (!targetUserId) {
      console.error('No user ID available for refreshing data');
      return;
    }

    // Refresh user object from Supabase Auth to get updated metadata
    const { data: authData, error: authError } = await supabase.auth.getUser();

    if (authError) {
      console.error('Error refreshing auth user data:', authError);
    } else if (authData?.user) {
      setUser(authData.user);
    }

    // Refresh database fields (tenant_id, role)
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('tenant_id, role')
      .eq('id', targetUserId)
      .maybeSingle();

    if (userError) {
      console.error('Error refreshing user data:', userError);
    } else if (userData) {
      setTenantId(userData.tenant_id);
      setRole(userData.role);
    }
  };

  // Sign in with email/password
  const signIn = async (email: string, password: string) => {
    // Set flags immediately to prevent auth state change handler from setting session
    isSigningInRef.current = true;
    setIsSigningIn(true);
    setIsCheckingTenantStatus(true);
    setIsLoading(true);

    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      isSigningInRef.current = false;
      setIsLoading(false);
      setIsSigningIn(false);
      setIsCheckingTenantStatus(false);
      console.error('Supabase signIn error:', error);
      throw error;
    }

    if (data.user) {
      // Fetch user role and check tenant status
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('tenant_id, role, tenant:tenants!users_tenant_id_fkey(status, deactivation_reason)')
        .eq('id', data.user.id)
        .maybeSingle();

      if (userError) {
        console.error('Error fetching user data:', userError);
        setIsCheckingTenantStatus(false);
      } else if (userData) {
        // Check if tenant is deactivated
        if (
          userData.tenant &&
          userData.tenant.status === 'inactive' &&
          userData.role !== 'system_admin'
        ) {
          const reason = userData.tenant.deactivation_reason || 'Account deactivated';

          // Sign out immediately
          await supabase.auth.signOut();

          // Reset flags before throwing error
          isSigningInRef.current = false;
          setIsLoading(false);
          setIsSigningIn(false);
          setIsCheckingTenantStatus(false);

          // Throw error to prevent success flow
          const deactivationError = new Error(
            `Your organization has been deactivated: ${reason}. Please contact support.`
          );
          deactivationError.name = 'TenantDeactivatedError';
          throw deactivationError;
        }

        // Set session state manually since we skipped it in auth state change
        setSession(data.session);
        setUser(data.user);
        setTenantId(userData.tenant_id);
        setRole(userData.role);
        setIsCheckingTenantStatus(false);

        // Identify user in LogRocket after successful sign in
        if (data.user.email) {
          logRocketService.identify(data.user.email, {
            userId: data.user.id,
            role: userData.role,
            tenantId: userData.tenant_id,
            signInMethod: 'sign_in',
            tenantStatus: userData.tenant?.status,
          });
        }
      }
    }

    isSigningInRef.current = false;
    setIsLoading(false);
    setIsSigningIn(false);
  };

  // Sign up with email/password
  const signUp = async (email: string, password: string) => {
    setIsLoading(true);
    const { error } = await supabase.auth.signUp({ email, password });
    setIsLoading(false);

    if (error) {
      throw error;
    }
  };

  // Sign out
  const signOut = async (options?: { skipNavigation?: boolean }) => {
    setIsLoading(true);
    const { error } = await supabase.auth.signOut();
    setIsLoading(false);

    if (error) {
      throw error;
    }

    setTenantId(null);
    setRole(null);

    // Only navigate if skipNavigation is not true
    if (!options?.skipNavigation) {
      navigate('/');
      toast.success('Signed out successfully');
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    setIsLoading(true);
    // Use VITE_FRONTEND_URL to ensure correct redirect URL for password reset
    // This prevents preview deployment URLs from being captured in emails
    const frontendUrl = import.meta.env.VITE_FRONTEND_URL;
    if (!frontendUrl) {
      throw new Error('VITE_FRONTEND_URL environment variable is not set');
    }
    const redirectTo = `${frontendUrl}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    setIsLoading(false);

    if (error) {
      throw error;
    }
  };

  // Create the value object for the context
  const value = {
    user,
    session,
    tenantId,
    role,
    isLoading,
    isCheckingTenantStatus,
    isSigningIn,
    isSystemAdmin: role === 'system_admin',
    isTenantAdmin: role === 'tenant_admin',
    isTenantInterviewer: role === 'tenant_interviewer',
    requiresPasswordReset: Boolean(user?.user_metadata?.force_password_reset),
    signIn,
    signUp,
    signOut,
    resetPassword,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

// Custom hook to check if user is authenticated
export const useRequireAuth = (redirectTo = '/login') => {
  const { user, isLoading } = useAuth();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        window.location.href = redirectTo;
      } else {
        setIsReady(true);
      }
    }
  }, [user, isLoading, redirectTo]);

  return { user, isReady };
};

export default useAuth;
